import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../config';
import { useAdminApi } from './hooks/useAdminApi';
import { AdminBreadcrumbs } from './components/AdminUiPrimitives';
import { AdminFilterBar, AdminPageHeader, FilterConfig, HeaderAction } from './components/AdminMasterDetail';
import ScheduleSessionsCalendar from './components/ScheduleSessionsCalendar';
import type { ScheduleSessionItem, SessionStatus } from './components/ScheduleSessionsPanel';

type RecurrenceType = 'None' | 'Weekly';
type LocationType = 'InPerson' | 'Online';
type ScheduleStatus = 'Scheduled' | 'Completed' | 'Cancelled';

interface ClassSchedule {
  id: number;
  organizationId: number;
  classroomId: number;
  classroomName: string;
  teacherUserId: number;
  teacherDisplayName: string;
  courseId?: number | null;
  courseName?: string | null;
  title: string;
  startDateTime: string;
  endDateTime: string;
  recurrenceType: RecurrenceType;
  recurrenceEndDate?: string | null;
  locationType: LocationType;
  location?: string | null;
  meetingUrl?: string | null;
  status: ScheduleStatus;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Classroom {
  id: number;
  name: string;
}

interface UserOption {
  userId: number;
  displayName: string;
  email?: string | null;
}

interface Course {
  id: number;
  name: string;
}

interface ConflictResponse {
  message: string;
}

const API_BASE_URL = `${config.apiBaseUrl}/api`;

const combineDateAndTime = (date: string, time: string): string => {
  const datePart = date.split('T')[0];
  const timePart = time.split('.')[0];
  return `${datePart}T${timePart}`;
};

const toDateInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toTimeInput = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
};

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const ManageSchedule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [teachers, setTeachers] = useState<UserOption[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);

  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);

  const [searchValue, setSearchValue] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [classroomFilter, setClassroomFilter] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  const selectedSchedule = useMemo(
    () => schedules.find((item) => item.id === selectedScheduleId) || null,
    [schedules, selectedScheduleId]
  );

  const isAllSessionsView = location.pathname.endsWith('/all');
  const isMySessionsView = location.pathname.endsWith('/mysessions');
  const isSessionRoute = isAllSessionsView || isMySessionsView;

  useEffect(() => {
    if (isSessionRoute) {
      setViewMode('list');
    }
  }, [isSessionRoute]);

  const mapSessionRow = (item: any): ClassSchedule => {
    const startDateTime = item.startDateTime
      ? item.startDateTime
      : (item.startDate && item.startTime
        ? combineDateAndTime(item.startDate, item.startTime)
        : (item.startDate ?? new Date().toISOString()));
    const endDateTime = item.endDateTime
      ? item.endDateTime
      : (item.endDate && item.endTime
        ? combineDateAndTime(item.endDate, item.endTime)
        : (item.endDate ?? new Date().toISOString()));
    return {
    id: item.id,
    organizationId: item.organizationId ?? 0,
    classroomId: item.classroomId ?? 0,
    classroomName: item.classroomName ?? '—',
    teacherUserId: item.teacherUserId ?? 0,
    teacherDisplayName: item.teacherDisplayName ?? '',
    title: item.title ?? 'Session',
    startDateTime,
    endDateTime,
    recurrenceType: item.recurrenceType ?? 'None',
    recurrenceEndDate: item.recurrenceEndDate ?? null,
    locationType: item.locationType ?? (item.meetingUrl ? 'Online' : 'InPerson'),
    location: item.location ?? null,
    meetingUrl: item.meetingUrl ?? null,
    status: item.status ?? 'Scheduled',
    notes: item.notes ?? null,
    isActive: item.isActive ?? true,
    createdAt: item.createdAt ?? new Date().toISOString(),
    updatedAt: item.updatedAt ?? new Date().toISOString(),
  };
  };

  const loadData = useCallback(async () => {
    if (!hasActiveOrganization) {
      setError('No organization selected. Please switch organization first.');
      setSchedules([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);

      if (isMySessionsView) {
        const myScheduleResponse = await axios.get<any[]>(`${API_BASE_URL}/schedules/my?upcomingOnly=false`, { headers });
        setSchedules(Array.isArray(myScheduleResponse.data) ? myScheduleResponse.data.map(mapSessionRow) : []);
        setClassrooms([]);
        setTeachers([]);
        setCourses([]);
        return;
      }

      if (isAllSessionsView) {
        const allScheduleResponse = await axios.get<any[]>(`${API_BASE_URL}/schedules`, { headers });
        const flattened = (Array.isArray(allScheduleResponse.data) ? allScheduleResponse.data : []).flatMap((schedule) => {
          const sessions = Array.isArray(schedule.sessions) ? schedule.sessions : [];
          return sessions.map((session: any) => mapSessionRow({
            ...session,
            title: schedule.title ?? 'Session',
            location: session.location ?? schedule.location ?? null,
            meetingUrl: session.meetingUrl ?? schedule.meetingUrl ?? null,
            recurrenceType: schedule.recurrenceType ?? 'None',
            recurrenceEndDate: schedule.recurrenceEndDate ?? null,
          }));
        });

        setSchedules(flattened);
        setClassrooms([]);
        setTeachers([]);
        setCourses([]);
        return;
      }

      const [scheduleResponse, classroomResponse, teacherResponse, courseResponse] = await Promise.all([
        axios.get<any[]>(`${API_BASE_URL}/schedules`, { headers }),
        axios.get<Classroom[]>(`${API_BASE_URL}/classrooms`, { headers }),
        axios.get<UserOption[]>(`${API_BASE_URL}/classrooms/users`, { headers }),
        axios.get<Course[]>(`${API_BASE_URL}/courses`, { headers }),
      ]);

      setSchedules(Array.isArray(scheduleResponse.data) ? scheduleResponse.data.map(mapSessionRow) : []);
      setClassrooms(Array.isArray(classroomResponse.data) ? classroomResponse.data : []);
      setTeachers(Array.isArray(teacherResponse.data) ? teacherResponse.data : []);
      setCourses(Array.isArray(courseResponse.data) ? courseResponse.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load schedule data.');
    } finally {
      setLoading(false);
    }
  }, [getManagementToken, getOrganizationHeaders, hasActiveOrganization, isAllSessionsView, isMySessionsView]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (schedules.length === 0) {
      if (selectedScheduleId !== null) {
        setSelectedScheduleId(null);
      }
      return;
    }

    const hasSelected = selectedScheduleId !== null && schedules.some((item) => item.id === selectedScheduleId);
    if (!hasSelected) {
      setSelectedScheduleId(schedules[0].id);
    }
  }, [schedules, selectedScheduleId]);

  const filteredRows = useMemo(() => {
    return schedules.filter((schedule) => {
      const search = searchValue.trim().toLowerCase();
      if (search) {
        const haystack = [
          schedule.title,
          schedule.classroomName,
          schedule.teacherDisplayName,
          schedule.location || '',
          schedule.meetingUrl || '',
          schedule.status,
        ].join(' ').toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (dateFilter) {
        const rowDate = toDateInput(schedule.startDateTime);
        if (rowDate !== dateFilter) {
          return false;
        }
      }

      if (classroomFilter && String(schedule.classroomId) !== classroomFilter) {
        return false;
      }

      if (teacherFilter && String(schedule.teacherUserId) !== teacherFilter) {
        return false;
      }

      if (statusFilter && schedule.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [schedules, searchValue, dateFilter, classroomFilter, teacherFilter, statusFilter]);

  const filterConfigs: FilterConfig[] = [
    {
      key: 'date',
      label: 'Date',
      value: dateFilter,
      onChange: setDateFilter,
      options: [
        { value: '', label: 'All Dates' },
        ...Array.from(new Set(schedules.map((item) => toDateInput(item.startDateTime))))
          .filter(Boolean)
          .map((value) => ({ value, label: value })),
      ],
    },
    {
      key: 'classroom',
      label: 'Classroom',
      value: classroomFilter,
      onChange: setClassroomFilter,
      options: [
        { value: '', label: 'All Classrooms' },
        ...classrooms.map((item) => ({ value: String(item.id), label: item.name })),
      ],
    },
    {
      key: 'teacher',
      label: 'Teacher',
      value: teacherFilter,
      onChange: setTeacherFilter,
      options: [
        { value: '', label: 'All Teachers' },
        ...teachers.map((item) => ({ value: String(item.userId), label: item.displayName })),
      ],
    },
    {
      key: 'status',
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: '', label: 'All Statuses' },
        { value: 'Scheduled', label: 'Scheduled' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' },
      ],
    },
  ];

  const beginCreate = () => {
    window.location.assign('/manage-schedule/new');
  };

  const beginView = (schedule: ClassSchedule) => {
    setSelectedScheduleId(schedule.id);
  };

  const cancelSchedule = async (schedule: ClassSchedule) => {
    const confirmed = window.confirm('Cancel this schedule?');
    if (!confirmed) {
      return;
    }

    try {
      setError(null);
      setMessage(null);
      const token = await getManagementToken();
      const headers = getOrganizationHeaders(token);
      await axios.post(`${API_BASE_URL}/schedules/${schedule.id}/cancel`, {}, { headers });
      setMessage('Schedule cancelled successfully.');
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to cancel schedule.');
    }
  };

  const sessionItems: ScheduleSessionItem[] = useMemo(
    () => schedules.map((row) => ({
      id: row.id,
      scheduleId: row.id,
      organizationId: row.organizationId,
      classroomId: row.classroomId,
      classroomName: row.classroomName,
      teacherUserId: row.teacherUserId,
      teacherDisplayName: row.teacherDisplayName,
      studentUserId: null,
      studentDisplayName: '',
      targetType: 'Classroom',
      startDateTime: row.startDateTime,
      endDateTime: row.endDateTime,
      status: row.status as SessionStatus,
      isModified: false,
      location: row.location,
      meetingUrl: row.meetingUrl,
      notes: row.notes,
    })),
    [schedules]
  );

  const actions: HeaderAction[] = [
    {
      key: 'all-sessions',
      label: 'All sessions',
      variant: 'secondary',
      onClick: () => navigate('/manage-schedule/all'),
    },
    {
      key: 'my-sessions',
      label: 'My sessions',
      variant: 'secondary',
      onClick: () => navigate('/manage-schedule/mysessions'),
    },
    {
      key: 'schedule-class',
      label: 'Schedule Class',
      variant: 'primary',
      onClick: beginCreate,
    },
    {
      key: 'refresh',
      label: 'Refresh',
      variant: 'secondary',
      onClick: () => {
        void loadData();
      },
    },
  ];

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12, gridTemplateRows: 'auto auto auto auto 1fr' }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          { label: 'Schedule', isCurrent: true },
        ]}
      />

      <AdminPageHeader
        title={isMySessionsView ? 'My Sessions' : isAllSessionsView ? 'All Sessions' : 'Schedule'}
        subtitle={isMySessionsView ? `${schedules.length} sessions for you` : isAllSessionsView ? `${schedules.length} total sessions` : `${schedules.length} total sessions`}
        actions={actions}
      />

      {!hasActiveOrganization && (
        <div style={{ borderRadius: 10, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', padding: '10px 12px', fontSize: '0.84rem' }}>
          No organization selected. Please switch organization first.
        </div>
      )}

      {(error || message) && (
        <div style={{ borderRadius: 10, border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`, background: error ? '#fff1f2' : '#eff6ff', color: error ? '#b91c1c' : '#1d4ed8', padding: '10px 12px', fontSize: '0.84rem' }}>
          {error || message}
        </div>
      )}

      {!isSessionRoute && (
        <AdminFilterBar
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder="Search title, classroom, teacher, location..."
          filters={filterConfigs}
        />
      )}

      {isSessionRoute && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="amd-btn"
            onClick={() => setViewMode('list')}
            style={{
              background: viewMode === 'list' ? '#1d4ed8' : '#fff',
              color: viewMode === 'list' ? '#fff' : '#1e293b',
              border: `1px solid ${viewMode === 'list' ? '#1d4ed8' : '#dfe8f8'}`,
            }}
          >
            List
          </button>
          <button
            type="button"
            className="amd-btn"
            onClick={() => setViewMode('calendar')}
            style={{
              background: viewMode === 'calendar' ? '#1d4ed8' : '#fff',
              color: viewMode === 'calendar' ? '#fff' : '#1e293b',
              border: `1px solid ${viewMode === 'calendar' ? '#1d4ed8' : '#dfe8f8'}`,
            }}
          >
            Calendar
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(520px, 58%) minmax(340px, 42%)', gap: 12, minHeight: 460 }}>
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', overflow: 'hidden', minHeight: 420, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159' }}>
            {isSessionRoute ? (viewMode === 'calendar' ? 'Session Calendar' : 'Session List') : 'Schedule List'}
          </div>
          {loading ? (
            <div style={{ padding: 14, color: '#475569' }}>Loading schedules...</div>
          ) : filteredRows.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, padding: 14, color: '#64748b' }}>No schedules found.</div>
          ) : isSessionRoute && viewMode === 'calendar' ? (
            <div style={{ padding: 12, flex: 1, minHeight: 300 }}>
              <ScheduleSessionsCalendar sessions={sessionItems} />
            </div>
          ) : (
            <div style={{ overflowX: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fbff', color: '#334155' }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Time</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Class</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Teacher</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Location</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const start = new Date(row.startDateTime);
                    const end = new Date(row.endDateTime);
                    return (
                      <tr key={row.id} style={{ borderBottom: '1px solid #edf2fb' }}>
                        <td style={{ padding: '8px 10px' }}>{start.toLocaleDateString()}</td>
                        <td style={{ padding: '8px 10px' }}>{`${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</td>
                        <td style={{ padding: '8px 10px' }}>{row.classroomName}</td>
                        <td style={{ padding: '8px 10px' }}>{row.teacherDisplayName}</td>
                        <td style={{ padding: '8px 10px' }}>{row.locationType === 'Online' ? (row.meetingUrl || 'Online') : (row.location || 'In Person')}</td>
                        <td style={{ padding: '8px 10px' }}>{row.status}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => beginView(row)} style={{ marginRight: 6 }}>Select</button>
                          <button type="button" className="amd-btn amd-btn-ghost" onClick={() => { window.location.assign(`/manage-schedule/${row.id}`); }} style={{ marginRight: 6 }}>Edit</button>
                          {!isSessionRoute && (
                            <button type="button" className="amd-btn amd-btn-ghost" onClick={() => { void cancelSchedule(row); }} disabled={row.status === 'Cancelled'}>Cancel</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 14, minHeight: 420, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ marginTop: 0, marginBottom: 10, color: '#173159' }}>{isSessionRoute ? 'Session Details' : 'Schedule Details'}</h3>

          {selectedSchedule ? (
            <div style={{ display: 'grid', gap: 8, fontSize: 14 }}>
              <div><strong>Title:</strong> {selectedSchedule.title}</div>
              <div><strong>Classroom:</strong> {selectedSchedule.classroomName}</div>
              <div><strong>Teacher:</strong> {selectedSchedule.teacherDisplayName}</div>
              <div><strong>Course:</strong> {selectedSchedule.courseName || '—'}</div>
              <div><strong>Start:</strong> {formatDateTime(selectedSchedule.startDateTime)}</div>
              <div><strong>End:</strong> {formatDateTime(selectedSchedule.endDateTime)}</div>
              <div><strong>Recurrence:</strong> {selectedSchedule.recurrenceType}{selectedSchedule.recurrenceType === 'Weekly' && selectedSchedule.recurrenceEndDate ? ` (until ${toDateInput(selectedSchedule.recurrenceEndDate)})` : ''}</div>
              <div><strong>Location:</strong> {selectedSchedule.locationType === 'Online' ? selectedSchedule.meetingUrl || 'Online' : selectedSchedule.location || 'In Person'}</div>
              <div><strong>Status:</strong> {selectedSchedule.status}</div>
              <div><strong>Notes:</strong> {selectedSchedule.notes || '—'}</div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="button"
                  className="amd-btn amd-btn-secondary"
                  onClick={() => {
                    window.location.assign(`/manage-schedule/${selectedSchedule.id}`);
                  }}
                >
                  Edit Details
                </button>
              </div>
            </div>
          ) : (
            <div style={{ color: '#64748b', fontSize: 14 }}>Select a row in the schedule grid to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageSchedule;
